import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type {
  GetTokenSymbolType,
  GetWalletHistoryType,
  UserBalancesType,
} from '@asetquhub/validation';
import {
  walletService,
  type TransferRequest,
  type TransferResponse,
} from '@/services/wallet.service';

/**
 * Query key factory for wallet-related queries
 */
export const walletKeys = {
  all: ['wallet'] as const,
  balances: () => [...walletKeys.all, 'balances'] as const,
  history: () => [...walletKeys.all, 'history'] as const,
  tokenSymbol: () => [...walletKeys.all, 'token-symbol'] as const,
  gasPrice: () => [...walletKeys.all, 'gas-price'] as const,
} as const;

/**
 * Hook to fetch token symbol
 * @param options - React Query options
 */
export function useTokenSymbol(
  options?: Omit<UseQueryOptions<GetTokenSymbolType | null, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: walletKeys.tokenSymbol(),
    queryFn: () => walletService.getTokenSymbol(),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    ...options,
  });
}

/**
 * Hook to fetch user balances (token and native)
 * @param options - React Query options
 */
export function useUserBalances(
  options?: Omit<UseQueryOptions<UserBalancesType | null, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: walletKeys.balances(),
    queryFn: () => walletService.getUserBalances(),
    staleTime: 1000 * 30, // Cache for 30 seconds
    refetchInterval: 1000 * 60, // Refetch every minute
    ...options,
  });
}

/**
 * Hook to fetch wallet transaction history
 * @param options - React Query options
 */
export function useWalletHistory(
  options?: Omit<UseQueryOptions<GetWalletHistoryType | null, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: walletKeys.history(),
    queryFn: () => walletService.getWalletHistory(),
    staleTime: 1000 * 30, // Cache for 30 seconds
    ...options,
  });
}

/**
 * Hook to fetch full wallet transaction history
 * @param options - React Query options
 */
export function useFullWalletHistory(
  options?: Omit<UseQueryOptions<GetWalletHistoryType | null, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...walletKeys.history(), 'full'],
    queryFn: () => walletService.getFullWalletHistory(),
    staleTime: 1000 * 30, // Cache for 30 seconds
    ...options,
  });
}

/**
 * Hook to fetch current gas price
 * @param options - React Query options
 */
export function useGasPrice(
  options?: Omit<UseQueryOptions<string | null, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: walletKeys.gasPrice(),
    queryFn: () => walletService.getGasPrice(),
    staleTime: 1000 * 15, // Cache for 15 seconds
    refetchInterval: 1000 * 30, // Refetch every 30 seconds
    ...options,
  });
}

/**
 * Hook to transfer tokens with wallet signature
 * @param options - Mutation options
 */
export function useTransfer(options?: {
  onSuccess?: (data: TransferResponse) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TransferRequest) => walletService.transfer(data),
    onSuccess: data => {
      // Invalidate balances and history to refetch
      queryClient.invalidateQueries({ queryKey: walletKeys.balances() });
      queryClient.invalidateQueries({ queryKey: walletKeys.history() });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}
